import { useEffect, useMemo, useState } from 'react';
import { X } from 'shared/icons';
import api from '../../services/api';
import StockBreakdownPanel from './StockBreakdownPanel';

const HIDDEN_DEPOSIT_IDS = [14886506404];

function toNumericId(value) {
    const parsed = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
}

const BlingStockModal = ({ product, warehouses = [], onClose }) => {
    const [productWarehouses, setProductWarehouses] = useState([]);
    const [loadingWarehouses, setLoadingWarehouses] = useState(false);
    const [productDetails, setProductDetails] = useState(null);

    useEffect(() => {
        if (!product?.sku) {
            setProductDetails(null);
            setProductWarehouses([]);
            return;
        }

        let active = true;
        const encodedSku = encodeURIComponent(product.sku);

        const fetchProductContext = async () => {
            setLoadingWarehouses(true);
            const [warehousesResult, detailsResult] = await Promise.allSettled([
                api.get(`/stock/products/${encodedSku}/warehouses`),
                api.get(`/stock/products/${encodedSku}`),
            ]);

            if (!active) {
                return;
            }

            const warehousesData =
                warehousesResult.status === 'fulfilled' ? warehousesResult.value : { warehouses: [] };
            const detailsData = detailsResult.status === 'fulfilled' ? detailsResult.value : null;

            setProductWarehouses(Array.isArray(warehousesData?.warehouses) ? warehousesData.warehouses : []);
            setProductDetails(detailsData && typeof detailsData === 'object' ? detailsData : null);
            setLoadingWarehouses(false);
        };

        fetchProductContext().catch((error) => {
            if (!active) {
                return;
            }
            console.error('Erro ao buscar contexto do produto:', error);
            setProductDetails(null);
            setProductWarehouses([]);
            setLoadingWarehouses(false);
        });

        return () => {
            active = false;
        };
    }, [product?.sku]);

    const warehouseNameById = useMemo(() => {
        const mapped = new Map();
        warehouses.forEach((warehouse) => {
            const id = toNumericId(warehouse?.id ?? warehouse?.warehouse_id ?? warehouse?.reference_id);
            if (!Number.isFinite(id)) {
                return;
            }
            mapped.set(id, warehouse.descricao || `Deposito ${id}`);
        });
        return mapped;
    }, [warehouses]);

    if (!product) {
        return null;
    }

    const resolvedProduct = {
        ...product,
        ...(productDetails || {}),
        name: product.name || productDetails?.nome || productDetails?.title || product.sku,
        code: product.code || productDetails?.codigo || productDetails?.sku || product.sku,
        bling_id: productDetails?.bling_id ?? product.bling_id ?? null,
        brand: productDetails?.brand ?? productDetails?.marca ?? product.brand ?? null,
        supplier_code: productDetails?.supplier_code ?? product.supplier_code ?? null,
        units_per_pallet: productDetails?.units_per_pallet ?? product.units_per_pallet ?? null,
    };

    const normalizedWarehouses = (productWarehouses || []).map((warehouse) => {
        const id = toNumericId(warehouse?.id ?? warehouse?.reference_id);
        const warehouseName =
            (Number.isFinite(id) ? warehouseNameById.get(id) : null) ||
            warehouse.name ||
            warehouse.descricao ||
            `Deposito ${id || '-'}`;

        return {
            id,
            reference_id: Number.isFinite(id) ? String(id) : String(warehouse?.reference_id || warehouse?.id || ''),
            name: warehouseName,
            quantity: Number(warehouse?.quantity || 0),
        };
    });

    const visibleBlingWarehouses = normalizedWarehouses.filter(
        (warehouse) => warehouse.quantity > 0 && !HIDDEN_DEPOSIT_IDS.includes(warehouse.id),
    );
    const blingVisibleStock = visibleBlingWarehouses.reduce(
        (sum, warehouse) => sum + Number(warehouse.quantity || 0),
        0,
    );
    const mlTotal = Number(resolvedProduct.mlStockData?.total ?? resolvedProduct.mlStock ?? 0);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <button
                    type="button"
                    aria-label="Fechar modal"
                    className="fixed inset-0 transition-opacity bg-mono-500 bg-opacity-75 dark:bg-mono-900 dark:bg-opacity-75"
                    onClick={onClose}
                />

                <div className="inline-block align-bottom bg-white dark:bg-black rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full border border-mono-200 dark:border-mono-800">
                    <div className="bg-mono-50 dark:bg-mono-950 px-6 py-4 border-b border-mono-200 dark:border-mono-800">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-mono-900 dark:text-white">Detalhes do produto</h3>
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-mono-400 hover:text-mono-500 dark:hover:text-mono-300 focus:outline-none"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="mb-6">
                            <h4 className="text-sm font-medium text-mono-900 dark:text-white mb-3">
                                Informacoes gerais
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Nome</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {resolvedProduct.name || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Codigo</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {resolvedProduct.code || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Bling ID</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {resolvedProduct.bling_id || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Marca</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {resolvedProduct.brand || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Supplier code</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {resolvedProduct.supplier_code || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Unid./pallet</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {resolvedProduct.units_per_pallet || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Estoque Bling (visivel)</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {blingVisibleStock} unidades
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">Estoque total ML</p>
                                    <p className="text-sm font-medium text-mono-900 dark:text-white">
                                        {mlTotal} unidades
                                    </p>
                                </div>
                            </div>
                        </div>

                        <StockBreakdownPanel
                            warehouses={normalizedWarehouses}
                            warehouseNameById={warehouseNameById}
                            mlStockData={resolvedProduct.mlStockData}
                            loading={loadingWarehouses}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlingStockModal;
